import { useMutation, useQueryClient } from "@tanstack/react-query"
import { z } from "zod"

import { useAxiosClientContext } from "@/components/core/AxiosInstanceProvider/useAxiosClient"
import { PaginationParams } from "@/components/pagination/pagination-state"
import { useQueryAPI } from "@/hooks/query-api"
import { useLogger } from "@/hooks/useLogger"

import { CampaignListResponseSchema } from "../campaigns-types"

export function useMutationChangeCampaignStatus(campaign_id: string) {
  const axiosClient = useAxiosClientContext()
  const queryClient = useQueryClient()
  const log = useLogger()

  return useMutation({
    mutationFn: async (isEnabled: boolean) => {
      const action = isEnabled ? "start" : "stop"

      return await axiosClient.post(`/campaign-filters/${action}/v2`, {
        campaign_id,
      })
    },
    onSuccess: async () => {
      // Wait until the case list is updated to show the right status when closing the dialog
      return await queryClient.invalidateQueries({ queryKey: ["campaigns"] })
    },
    onError: (error) => {
      log.error(error)
    },
  })
}

export function useMutationCloseCampaign() {
  const axiosClient = useAxiosClientContext()
  const queryClient = useQueryClient()
  const log = useLogger()

  return useMutation({
    mutationFn: async (campaign_id: string) => {
      return await axiosClient.post(`/campaign-filters/${campaign_id}/close`)
    },
    onSuccess: async () => {
      // Wait until the case list is updated to show the right status when closing the dialog
      return await queryClient.invalidateQueries({ queryKey: ["campaigns"] })
    },
    onError: (error) => {
      log.error(error)
    },
  })
}

export function useFetchCampaigns({
  enabled_campaigns_only,
  exclude_closed_campaigns,
  page,
  pageSize,
  search_query,
}: {
  enabled_campaigns_only: boolean
  exclude_closed_campaigns: boolean
  search_query: string
} & PaginationParams) {
  return useQueryAPI({
    apiEndPoint: "/campaign-filters/get-campaigns",
    queryKey: ["campaigns"],
    params: {
      enabled_campaigns_only,
      exclude_closed_campaigns,
      page,
      page_size: pageSize,
      search_string: search_query,
    },
    schema: CampaignListResponseSchema,
  })
}

export function useFetchAllCampaignNames() {
  return useQueryAPI({
    apiEndPoint: "/campaign-filters/get-campaigns",
    queryKey: ["allCampaignNames"],
    params: {
      format: "compact",
    },
    schema: z.object({
      data: z.object({
        campaigns: z.array(z.object({ id: z.string() })),
        total: z.number(),
      }),
    }),
  })
}
